import { MenuConfiguration, MenuItem } from '@newgenus/common';

/**
 * Mobile primary-navigation helpers.
 *
 * The phone bottom bar is **not** a mirror of the section list — it holds the
 * user's chosen favourites plus a persistent "Menu" slot (which opens the full
 * sections→items sheet). These pure helpers turn the permission-filtered
 * {@link MenuConfiguration} (from `SecurityService.generateSubMenuFromUserAccess`)
 * into bar items, and resolve/seed the favourites preference. Kept framework-free
 * so the logic is unit-testable without Firestore or the DOM.
 */

/** Max favourite slots a user may pin (the bar also always carries "Menu"). */
export const MAX_FAVOURITES = 4;

/** How many favourites we seed by default before the user customises. */
export const DEFAULT_FAVOURITE_COUNT = 3;

/** Menu ids of the two workspace baskets that carry a live badge count. */
export const INBASKET_ITEM_ID = 'basket-1';
export const PENDING_ITEM_ID = 'basket-2';

/** Preferred default destinations, in priority order, when the user has access. */
const PREFERRED_DEFAULT_IDS = [INBASKET_ITEM_ID, 'designer']; // Inbasket, Org Board

/** The persistent "Menu" bar item — opens the full navigation sheet, never routes. */
export const MOBILE_MENU_ITEM: MenuItem = {
    id: 'mobile-menu',
    name: 'Menu',
    section: '',
    path: '',
    icon: 'menu',
    count: 0,
    showCount: false,
    isHighlighted: false,
    order: 99999,
};

/** The "Home" entry surfaced inside the Menu sheet (Home is folded into Menu). */
export const MOBILE_HOME_ITEM: MenuItem = {
    id: 'mobile-home',
    name: 'Home',
    section: '',
    path: '',
    icon: 'home',
    count: 0,
    showCount: false,
    isHighlighted: false,
    order: 0,
};

/**
 * One destination per non-HOME section, routing to that section's landing
 * (`path: ''` → `app/<section>`). Derived from the section key rather than the
 * HOME clones, so ids are stable (`section:<slug>`) and never collide with leaves.
 */
export function sectionDestinations(menu: MenuConfiguration): MenuItem[] {
    return Object.entries(menu)
        .filter(([key]) => key !== 'HOME')
        .map(([key, cfg]) => {
            const first = cfg.topTierMenuItems[0] ?? cfg.middleTierMenuItems[0];
            const section = first?.section ?? key.toLowerCase();
            return {
                id: `section:${section}`,
                name: cfg.header,
                section,
                path: '',
                icon: first?.icon ?? 'folder',
                count: 0,
                showCount: false,
                isHighlighted: false,
                order: first?.order ?? 0,
            } as MenuItem;
        });
}

/** Every leaf item (top + middle tier) across all non-HOME sections. */
export function leafDestinations(menu: MenuConfiguration): MenuItem[] {
    return Object.entries(menu)
        .filter(([key]) => key !== 'HOME')
        .flatMap(([, cfg]) => [...cfg.topTierMenuItems, ...cfg.middleTierMenuItems]);
}

/** Sections first, then every leaf — the full set a user may pin or browse. */
export function allDestinations(menu: MenuConfiguration): MenuItem[] {
    return [...sectionDestinations(menu), ...leafDestinations(menu)];
}

/**
 * Map favourite ids → their items, in the saved order, dropping any id the user
 * no longer has access to (permission intersection) and capping at `max`.
 */
export function resolveFavouriteItems(all: MenuItem[], favIds: string[], max = MAX_FAVOURITES): MenuItem[] {
    const byId = new Map(all.map((item) => [item.id, item]));
    return favIds
        .map((id) => byId.get(id))
        .filter((item): item is MenuItem => !!item)
        .slice(0, max);
}

/**
 * Seed a sensible default favourites set from what the user can access: prefer
 * Inbasket + Org Board, then fill from the remaining destinations. Only ever
 * returns ids present in `all` (so it respects per-user section access).
 */
export function defaultFavouriteIds(all: MenuItem[], count = DEFAULT_FAVOURITE_COUNT): string[] {
    const availableIds = all.map((d) => d.id);
    const picked: string[] = PREFERRED_DEFAULT_IDS.filter((id) => availableIds.includes(id));
    for (const item of all) {
        if (picked.length >= count) break;
        if (!picked.includes(item.id)) picked.push(item.id);
    }
    return picked.slice(0, count);
}

/** Assemble the bottom-bar list: favourites first, the Menu slot last. */
export function buildBottomBarItems(favouriteItems: MenuItem[], menuItem: MenuItem = MOBILE_MENU_ITEM): MenuItem[] {
    return [...favouriteItems, menuItem];
}

/** Live badge values, keyed by {@link MenuItem} id (e.g. `{ 'basket-1': 4 }`). */
export type MenuBadgeCounts = Record<string, number>;

/**
 * Stamp live counts onto bar items, by id (OE-97).
 *
 * Two things make this a copy rather than a write:
 *
 * 1. The bar renders under `ChangeDetectionStrategy.OnPush`, so a count written
 *    in place on an item it already holds never reaches the DOM — the badge
 *    keeps whatever it rendered first. That is exactly how the phone's Inbasket
 *    badge sat at 0 no matter how much mail was in the basket.
 * 2. The items are the shared `MenuConfigurations` objects (the permission
 *    filter keeps their references), which the sidebar also mutates. Cloning
 *    keeps the bar a read-only consumer instead of a third party in that
 *    tug-of-war.
 *
 * Ids with no entry in `counts` keep their existing value.
 */
export function applyBadgeCounts(items: MenuItem[], counts: MenuBadgeCounts): MenuItem[] {
    return items.map((item) => (item.id in counts ? { ...item, count: counts[item.id] } : { ...item }));
}
